import React, { useState, useEffect } from 'react';

import { bemCssModules } from '@utils';
import { useInterval, useLocation, useNavigate } from '@utils/hooks';
import { LocationStateType } from '@utils/types';

import { buyActions, cardsActions, operationBlockActions, useStore } from '@modules';
import { EPaymentStatuses } from '@modules/transactions/types';
import { OperationNextEnum } from '@modules/types';

import Spinner from '@components/Spinner';
import Card3DSFlow from '@containers/Card3DSFlow';
import { DataKeysEnum } from '@containers/OperationBlock/types';
import { getOperations } from '@selectors';

import style from './VerifyCard.scss';

const cn = bemCssModules(style, 'VerifyCard');

export type TVerifyCard = {
  onSuccessFlow: (bindStatus?: string) => void;
};

export const VerifyCard: CustomFC<TVerifyCard> = props => {
  const { className, onSuccessFlow } = props;

  const navigate = useNavigate();
  const [store, dispatch] = useStore();
  const { cards, buy } = store;
  const [intervalCounter, setIntervalCounter] = useState<number>(0);
  const [iFrameIsLoad, setIFrameIsLoad] = useState(false);
  const isLoading =
    !!buy.loaders.getBuyStatus ||
    !!buy.loaders.getBuyStatusPolling ||
    !!cards.loaders.getBindStatus ||
    !!cards.loaders.getBindStatusPolling;
  const location = useLocation<LocationStateType['verifyCard']>();
  const { state } = location;
  const operations = getOperations(store);

  const { bindId, bindStatus, card3DS } = cards;
  const { buyId, buyStatus } = buy;
  const intervalShouldBeUpdated = intervalCounter < CONST.getBindStatusMaxCount;

  const isTopup = buyStatus && buyStatus !== EPaymentStatuses.pending && buyId;
  const isCardsBind = bindStatus && bindStatus !== CONST.card3DS.statuses.pending && !buyId;
  const currentInterval = buyId ? CONST.getStatusInterval : CONST.getBindCardStatusInterval;

  useEffect(() => {
    if (card3DS.passed && (isTopup || isCardsBind)) onSuccessFlow(bindStatus);
  }, [card3DS.passed, bindStatus, buyStatus]);

  const getStatus = () => {
    const bindStatusIsCompleted = !bindStatus || bindStatus === CONST.card3DS.statuses.pending;
    const buyStatusIsCompleted = buyStatus === EPaymentStatuses.pending;

    if (bindStatusIsCompleted && bindId && !buyId) {
      dispatch(cardsActions.getBindStatusPolling(bindId))
        .unwrap()
        .then(res => {
          if (res.data.status === CONST.card3DS.statuses.failed) onSuccessFlow(res.data.status);
          if (res.data.status === CONST.card3DS.statuses.succeeded) {
            const left = { group: DataKeysEnum.cards, value: res.data.id };
            if (!operations.sell_with_new_card) dispatch(operationBlockActions.setLeft(left));
          }
        });
      setIntervalCounter(prev => prev + 1);
    }

    if (card3DS.passed && buyStatusIsCompleted && buyId) {
      dispatch(buyActions.getBuyStatusPolling(buyId))
        .unwrap()
        .then(({ data }) => {
          if (data.next === OperationNextEnum.verifyDescriptor) {
            navigate<'descriptor'>(CONST.nav.verifyDescriptor, { state: { id: buyId, ...state } });
          }
        });
      setIntervalCounter(prev => prev + 1);
    }
  };

  useInterval({
    callback: getStatus,
    shouldStop: isLoading || !intervalShouldBeUpdated,
    delay: currentInterval,
  });

  useEffect(() => {
    return () => {
      dispatch(cardsActions.set3DSPassed(false));
    };
  }, []);

  const startInterval = () => {
    setIntervalCounter(0);
  };

  const onFrameLoaded = (status: boolean) => {
    setIFrameIsLoad(status);
  };

  return (
    <div className={cn(null, [className])}>
      <div className={cn('before-iframe', { hidden: iFrameIsLoad || card3DS.passed })}>
        <Spinner className={cn('preloader')} />
      </div>
      <Card3DSFlow
        className={cn('cards-3ds')}
        onSuccess={startInterval}
        onFrameLoaded={onFrameLoaded}
      />
    </div>
  );
};

export default VerifyCard;
